import React, {useEffect, useMemo} from 'react';
import {
  useApolloClient,
  ApolloClient,
  type NormalizedCacheObject,
} from '@apollo/client';

import {useLocation} from 'react-router-dom';
import {useSafeNavigate as useNavigate} from './util/safeNavigate';
import {useAppDispatch, useAppSelector} from './state/hooks';
import {updateLogin, reset as resetAuth, getCodeChallenge} from './state/auth';
import {reset as resetBaseUrl} from './state/baseUrl';
import {useAppConfig, APP_CONFIG} from './net/query/AppConfig.gql';
import {useLogin} from './net/query/Login.gql';
import {type AppConfigQuery} from './net/types/graphql';
import {LoadingContainer} from './LoadingContainer';
import {Layout, Content} from './components/ui/layout';
import {Button} from './components/ui/button';
import {useToast} from './components/ui/toast';

const doLogin = async (
  client: ApolloClient<NormalizedCacheObject>,
  codeVerifier: string,
  showToast: (
    message: string,
    type: 'error' | 'success' | 'info' | 'warning',
  ) => void,
) => {
  // Refetch the app config because we need a fresh state token, which will
  // expire after just a couple minutes. So if the browser sits idle for half
  // an hour they won't be able to log in.
  const [configResult] = await client.refetchQueries({
    include: [APP_CONFIG],
  });
  if (!configResult.data) {
    showToast(
      "Can't log you in! " + configResult.error || 'unknown error',
      'error',
    );
    return;
  }
  const configData = configResult.data as AppConfigQuery;
  const config = configData.clientConfig!.oauth2!;

  const url = new URL(config.authEndpoint);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('redirect_uri', config.webClient.redirectUrl);
  url.searchParams.set('client_id', config.webClient.clientId);
  url.searchParams.set('scope', config.scopes.join(' '));
  url.searchParams.set('state', config.state);
  url.searchParams.set('code_challenge', await getCodeChallenge(codeVerifier));
  url.searchParams.set('code_challenge_method', 'S256');
  if (config.accessType) {
    url.searchParams.set('access_type', config.accessType);
  }

  // Delegate to the OAuth2 provider
  window.location.href = url.toString();
};

export const Login = () => {
  const apolloClient = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {showToast} = useToast();
  const {codeVerifier, loggedIn} = useAppSelector(({auth}) => auth);
  const baseUrl = useAppSelector(({baseUrl}) => baseUrl.value);
  const {data, loading, error} = useAppConfig();
  const [login, lr] = useLogin();
  // Check the `from` search parameter
  const {search} = useLocation();
  const query = useMemo(() => new URLSearchParams(search), [search]);
  const fromParam = query.get('from');
  // Check if the user is loading from an OAuth2 redirect. In this case,
  // we don't want to show the login button, just the loading spinner.
  const loadingFromOAuth2 = fromParam === 'oauth2';

  useEffect(() => {
    const handleEffect = async () => {
      if (!baseUrl) {
        await navigate('/setup');
        return;
      }

      if (loading || error) {
        return;
      }

      if (!lr.loading && !lr.error && lr.data?.login) {
        dispatch(updateLogin(lr.data.login));
        // TODO(jnu): get redirect URI from state
        await navigate('/');
        return;
      }

      // If the login query hasn't been issued yet but the user appears to be
      // logged in (per local state), make the request to verify the auth info
      // with the server.
      if (loggedIn) {
        login();
        return;
      }
    };

    handleEffect();
  }, [loading, error, baseUrl, loggedIn, lr.data, lr.loading, lr.error]);

  const logo = data?.clientConfig?.style?.login?.logo;

  return (
    <LoadingContainer
      loading={loading || lr.loading || loadingFromOAuth2}
      error={error}
      data={data}
    >
      <Layout id="nudge-root">
        <Content
          id="nudge-body"
          className="flex items-center justify-center min-h-screen"
        >
          <div className="w-full max-w-md space-y-6 p-6">
            {logo && (
              <div className="flex justify-center">
                <img className="w-1/2 rounded-lg" src={logo} alt="Nudge Logo" />
              </div>
            )}
            <p className="text-center text-2xl font-bold text-secondary">
              Nudge Reminders
            </p>
            <div className="flex flex-col space-y-4">
              <Button
                className="w-full cursor-pointer"
                onClick={() => doLogin(apolloClient, codeVerifier, showToast)}
                disabled={!data?.clientConfig?.oauth2}
              >
                {data?.clientConfig?.style?.login?.signInText || 'Login'}
              </Button>
              <Button
                variant="outline"
                className="w-full cursor-pointer"
                onClick={() => {
                  dispatch(resetAuth());
                  dispatch(resetBaseUrl());
                }}
              >
                Reset
              </Button>
            </div>
          </div>
        </Content>
      </Layout>
    </LoadingContainer>
  );
};
